import React, { useRef, useState } from 'react'
import SubmitButton from '../../../../uiKit/buttons/SubmitButton'
import { alertError } from '../../../../api'
import { selectedFile } from '../../../flowBuilder/api/fileService'
import { confirmResultsDelivery } from '../../api/contactInfoApi'
import {
  ConfirmResultsDeliveryBody,
  ResultsDeliveryInfo,
  ScheduleResultFileType,
  ScheduleResultTexts,
} from '../../api/model/ContactInfo'
import classes from './styles.module.scss'
import FileDropZone from './components/FileDropZone'

const cyrillicPattern = /[\u0400-\u04FF]/

interface ConfirmResultsDeliveryModalProps {
  match: any
  onClose: () => void
  resultsDeliveryInfo: ResultsDeliveryInfo
}

export const ConfirmResultsDeliveryModal: React.FC<ConfirmResultsDeliveryModalProps> = ({
  match,
  onClose,
  resultsDeliveryInfo,
}) => {
  const isSubmittingRef = useRef(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [filesUploading, setFilesUploading] = useState({
    labResults: false,
    familyCommunicationLetter: false,
    gtResultsLetter: false,
  })
  const [userFile, setUserFile] = useState({
    labResults: null,
    familyCommunicationLetter: null,
    gtResultsLetter: null,
  })
  const [filesUrl, setFilesUrl] = useState({
    labResults: null,
    familyCommunicationLetter: null,
    gtResultsLetter: null,
  })
  const [uploadProgress, setUploadProgress] = useState({
    labResults: 0,
    familyCommunicationLetter: 0,
    gtResultsLetter: 0,
  })
  const [fileValidation, setFileValidation] = useState({
    labResults: true,
    familyCommunicationLetter: true,
    gtResultsLetter: true,
  })

  const hasGTResultsLetter = resultsDeliveryInfo.geneticTestLetterAttached || !!filesUrl.gtResultsLetter
  const hasFamilyCommunicationLetter =
    resultsDeliveryInfo.familyCommunicationLetterAttached || !!filesUrl.familyCommunicationLetter
  const isUploading = Object.values(filesUploading).some(Boolean)

  const handleUpload = (fileType: string) => {
    return setInterval(() => {
      setUploadProgress(prev => {
        const progress = prev[fileType] + prev[fileType] * 0.5 + 1

        return {
          ...prev,
          [fileType]: Math.min(progress, 95),
        }
      })
    }, 300)
  }

  const handleFileChange = (event, fileType: string) => {
    const file = event.target.files[0]

    if (!file) {
      return
    }

    const preparedFileName = file.name.replace(/[^\w\s.\/]/g, '_').replace(/\s/g, '')
    const newFile = new File([file], preparedFileName, { type: file.type })
    if (file.size < 10 * 1024 * 1024) {
      if (cyrillicPattern.test(file.name)) {
        return alertError('File name can only contain Latin letters and numbers.')
      }
      const uploadInterval = handleUpload(fileType)
      setFilesUploading(prevState => ({
        ...prevState,
        [fileType]: true,
      }))
      setUserFile(prevState => ({
        ...prevState,
        [fileType]: file,
      }))
      setFileValidation(prevState => ({
        ...prevState,
        [fileType]: true,
      }))

      const fileToUpload = new FormData()
      fileToUpload.append('file', newFile)
      selectedFile(fileToUpload).then(res => {
        clearInterval(uploadInterval)
        setFilesUploading(prevState => ({
          ...prevState,
          [fileType]: false,
        }))

        if (!res || !res.url) {
          setUserFile(prevState => ({
            ...prevState,
            [fileType]: null,
          }))
          setUploadProgress(prevState => ({
            ...prevState,
            [fileType]: 0,
          }))
          return
        }

        setFilesUrl(prevState => ({
          ...prevState,
          [fileType]: res.url,
        }))
        setUploadProgress(prevState => ({
          ...prevState,
          [fileType]: 100,
        }))
      })
    } else {
      alertError('File size exceeds the 10MB limit.')
    }
  }

  const handleDeleteFile = (fileType: string) => {
    setUserFile(prevState => ({
      ...prevState,
      [fileType]: null,
    }))
    setFilesUrl(prevState => ({
      ...prevState,
      [fileType]: null,
    }))
    setUploadProgress(prevState => ({
      ...prevState,
      [fileType]: 0,
    }))
    setFilesUploading(prevState => ({
      ...prevState,
      [fileType]: false,
    }))
  }

  const handleSubmitButtonClick = () => {
    if (isUploading || isSubmittingRef.current) {
      return
    }

    setFileValidation(prevState => ({
      ...prevState,
      gtResultsLetter: hasGTResultsLetter,
      familyCommunicationLetter: hasFamilyCommunicationLetter,
    }))

    if (!hasGTResultsLetter || !hasFamilyCommunicationLetter) {
      return
    }

    const body: ConfirmResultsDeliveryBody = {}
    if (filesUrl.labResults) {
      body.labResultsURL = filesUrl.labResults
    }
    if (filesUrl.gtResultsLetter) {
      body.geneticTestLetterURL = filesUrl.gtResultsLetter
    }
    if (filesUrl.familyCommunicationLetter) {
      body.familyCommunicationLetterURL = filesUrl.familyCommunicationLetter
    }

    isSubmittingRef.current = true
    setIsSubmitting(true)

    confirmResultsDelivery(match.params.botId, match.params.contactId, body).then(
      response => {
        if (response) {
          onClose()
          return
        }

        isSubmittingRef.current = false
        setIsSubmitting(false)
      },
      () => {
        isSubmittingRef.current = false
        setIsSubmitting(false)
      },
    )
  }

  return (
    <div className={classes.modalWrapper}>
      <h2 className={classes.header}>
        Upload documents for positive result
      </h2>
      <FileDropZone
        title={ScheduleResultTexts.labResultsTitle}
        subTitle={ScheduleResultTexts.labResultsSubTile}
        isFileValid={fileValidation.labResults}
        onChange={e => handleFileChange(e, ScheduleResultFileType.LAB_RESULTS)}
        handleDeleteFile={() => handleDeleteFile(ScheduleResultFileType.LAB_RESULTS)}
        uploadProgress={uploadProgress.labResults}
        selectedUserFile={userFile.labResults}
      />
      <FileDropZone
        title={ScheduleResultTexts.gtResultsLetterTitle}
        subTitle={ScheduleResultTexts.gtResultsLetterSubTitle}
        isFileValid={fileValidation.gtResultsLetter || hasGTResultsLetter}
        onChange={e => handleFileChange(e, ScheduleResultFileType.GT_RESULTS_LETTER)}
        handleDeleteFile={() => handleDeleteFile(ScheduleResultFileType.GT_RESULTS_LETTER)}
        uploadProgress={uploadProgress.gtResultsLetter}
        selectedUserFile={userFile.gtResultsLetter}
      />
      <FileDropZone
        title={ScheduleResultTexts.familyCommunicationLetterTitle}
        subTitle={ScheduleResultTexts.familyCommunicationLetterSubTitle}
        isFileValid={fileValidation.familyCommunicationLetter || hasFamilyCommunicationLetter}
        onChange={e => handleFileChange(e, ScheduleResultFileType.FAMILY_COMMUNICATION_LETTER)}
        handleDeleteFile={() => handleDeleteFile(ScheduleResultFileType.FAMILY_COMMUNICATION_LETTER)}
        uploadProgress={uploadProgress.familyCommunicationLetter}
        selectedUserFile={userFile.familyCommunicationLetter}
      />
      <SubmitButton
        disabled={isUploading || isSubmitting}
        styles={{
          width: '97px',
          height: '48px',
          margin: '30px auto 0',
          display: 'block',
        }}
        onClick={handleSubmitButtonClick}
        title={'Send'}
      />
    </div>
  )
}
